import React from 'react';
import type { Notification, Page } from '../types.ts';
import NotificationItem from './NotificationItem.tsx';
import { Cog6ToothIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

interface NotificationsPanelProps {
    notifications: Notification[];
    onClose: () => void;
    onNavigate: (page: Page, initialState?: any) => void;
    onMarkAllAsRead: () => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ notifications, onClose, onNavigate, onMarkAllAsRead }) => {
    const handleSettingsClick = () => {
        onNavigate('Settings', { initialTab: 'notifications' });
        onClose();
    };

    return (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 origin-top-right rounded-2xl bg-surface shadow-xl border border-border/30 focus:outline-none z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border/20 bg-gray-50/30">
                <h3 className="text-base font-semibold text-text">Notifications</h3>
                <div className="flex items-center space-x-2">
                    <button onClick={onMarkAllAsRead} className="text-xs font-medium text-primary hover:underline flex items-center gap-1">
                        <CheckCircleIcon className="h-4 w-4"/> Mark all as read
                    </button>
                    <button onClick={handleSettingsClick} title="Notification Settings" className="p-1.5 text-text-subtle hover:bg-gray-100 rounded-full">
                        <Cog6ToothIcon className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Body */}
            <div className="max-h-96 overflow-y-auto p-2">
                {notifications.length > 0 ? (
                    <ul>
                        {notifications.map(notification => (
                            <li key={notification.id}>
                                <NotificationItem 
                                    notification={notification}
                                    onNavigate={onNavigate}
                                    onClose={onClose}
                                />
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="text-center py-12 px-4">
                        <p className="text-sm font-semibold text-text">All caught up!</p>
                        <p className="text-xs text-text-subtle mt-1">You have no new notifications.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsPanel;
